import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, apiError } from "@/lib/api";
import { studentCopy } from "@/lib/studentI18n";

export function useStudySession(packId, refreshAuth, language = "es") {
  const [session, setSession] = useState(null);
  const [items, setItems] = useState([]);
  const [index, setIndex] = useState(0);
  const [error, setError] = useState("");
  const [feedback, setFeedback] = useState(null);
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState(null);
  const [resumed, setResumed] = useState(false);
  const [startedAt, setStartedAt] = useState(Date.now());
  const [pairs, setPairs] = useState({});
  const [activeLeft, setActiveLeft] = useState(null);
  const [choice, setChoice] = useState(null);
  const [textAnswer, setTextAnswer] = useState("");
  const [orderedItems, setOrderedItems] = useState([]);
  const startingRef = useRef(false);

  const begin = useCallback(async (mode = "standard", count = 20, restart = false) => {
    if (startingRef.current) return;
    startingRef.current = true;
    setError("");
    setSession(null);
    try {
      const res = await api.post("/study/sessions", { study_pack_id: packId, question_count: count, mode, restart });
      setSession(res.data.session);
      setResumed(Boolean(res.data.resumed));
      setItems(res.data.items);
      const firstUnanswered = res.data.items.findIndex((item) => !item.answered);
      setIndex(firstUnanswered === -1 ? res.data.items.length - 1 : firstUnanswered);
    } catch (err) {
      setError(apiError(err, studentCopy(language).startError));
    } finally {
      startingRef.current = false;
    }
  }, [packId, language]);

  useEffect(() => {
    begin();
  }, [begin]);

  const current = items[index];
  const currentQuestionId = current?.question?.id;
  const currentQuestionType = current?.question?.type;
  const currentOrderingItems = current?.question?.payload?.items;

  useEffect(() => {
    setFeedback(null);
    setChoice(null);
    setTextAnswer("");
    setOrderedItems(currentQuestionType === "ordering" ? [...(currentOrderingItems || [])] : []);
    setPairs({});
    setActiveLeft(null);
    setStartedAt(Date.now());
  }, [index, currentQuestionId, currentQuestionType, currentOrderingItems]);

  const answerPayload = useMemo(
    () => buildAnswerPayload(current, { choice, pairs, textAnswer, orderedItems }),
    [current, choice, pairs, textAnswer, orderedItems]
  );

  const submit = async () => {
    if (!answerPayload || busy) return;
    setBusy(true);
    try {
      const res = await api.post(`/study/sessions/${session.id}/answers`, {
        question_id: current.question.id,
        answer: answerPayload,
        time_spent_ms: Date.now() - startedAt,
      });
      setFeedback(res.data);
      setItems((previous) => previous.map((item, itemIndex) => (
        itemIndex === index ? { ...item, answered: true, is_correct: res.data.is_correct } : item
      )));
      setSession((previous) => ({
        ...previous,
        answered_count: res.data.answered_count,
        correct_count: res.data.correct_count,
      }));
    } catch (err) {
      setError(apiError(err, studentCopy(language).answerError));
    } finally {
      setBusy(false);
    }
  };

  const next = async () => {
    if (index + 1 < items.length) {
      setIndex(index + 1);
      return;
    }
    setBusy(true);
    try {
      const res = await api.post(`/study/sessions/${session.id}/complete`);
      setResults(res.data);
      await refreshAuth();
    } catch (err) {
      setError(apiError(err, studentCopy(language).finishError));
    } finally {
      setBusy(false);
    }
  };

  const extend = async (additional, mode) => {
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      const res = await api.post(`/study/sessions/${session.id}/extend`, { additional, mode });
      setSession(res.data.session);
      setItems(res.data.items);
      setResults(null);
      const firstUnanswered = res.data.items.findIndex((item) => !item.answered);
      setIndex(firstUnanswered === -1 ? 0 : firstUnanswered);
    } catch (err) {
      setError(apiError(err, studentCopy(language).noMoreExercises));
    } finally {
      setBusy(false);
    }
  };

  return {
    session,
    items,
    index,
    error,
    feedback,
    busy,
    results,
    resumed,
    pairs,
    setPairs,
    activeLeft,
    setActiveLeft,
    choice,
    setChoice,
    textAnswer,
    setTextAnswer,
    orderedItems,
    setOrderedItems,
    current,
    answerPayload,
    begin,
    submit,
    next,
    extend,
  };
}

export function buildAnswerPayload(current, answers) {
  if (!current) return null;
  const type = current.question.type;
  if (type === "multiple_choice") return answers.choice === null ? null : { selected_index: answers.choice };
  if (type === "true_false") return answers.choice === null ? null : { value: answers.choice === 1 };
  if (type === "fill_blank") return answers.textAnswer.trim() ? { text: answers.textAnswer.trim() } : null;
  if (type === "ordering") return answers.orderedItems.length ? { items: answers.orderedItems } : null;
  if (type === "matching") {
    const lefts = current.question.payload.lefts || [];
    if (lefts.some((left) => !answers.pairs[left])) return null;
    return { pairs: lefts.map((left) => ({ left, right: answers.pairs[left] })) };
  }
  return null;
}
